import styled from '@emotion/styled';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DescriptionIcon from '@mui/icons-material/Description';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import { Box, ButtonBase, IconButton, Link, Popover, Tooltip, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import type { CopyFeedback } from '../hooks/useCopyFeedback';
import { useCopyFeedback } from '../hooks/useCopyFeedback';
import { useMessageFeedbackSelection } from '../hooks/useMessageFeedbackSelection';
import type { DownloadLink } from '../models';
import {
  countDownloadLinks,
  getDownloadLinkDisplayName,
  getDownloadLinkKey,
  hasDownloadLinks,
  splitDownloadLinks,
} from '../services';
import {
  colorAgentBorder,
  colorAgentTextSecondary,
  colorNeutralGray,
  colorSemanticSuccess,
  fontSizeXs,
  fontWeightSemibold,
  space1,
  space2,
} from '../styles/tokens';

interface Props {
  links?: DownloadLink[];
  content?: string;
}

// Single clean action row, ChatGPT-style — kept visually light (no heavy panel).
const ActionRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${space1};
  margin-top: ${space1};
`;

const ActionButton = styled(IconButton)`
  padding: 2px;
  color: ${colorAgentTextSecondary};
`;

const ActionIcon = styled.span`
  font-size: 15px;
`;

// Compact pill that opens the source/file popover. ButtonBase (not IconButton)
// so it sizes to its icon+text content instead of forcing a circular icon box.
const SourceButton = styled(ButtonBase)`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 22px;
  padding: 0 8px;
  border: 1px solid ${colorAgentBorder};
  border-radius: 11px;
  color: ${colorAgentTextSecondary};
  font-size: ${fontSizeXs};
  line-height: 1;
  white-space: nowrap;

  &:hover {
    background-color: ${colorNeutralGray};
    border-color: ${colorAgentTextSecondary};
  }

  & .source-icon {
    font-size: 14px;
  }
`;

const MenuPaper = styled(Box)`
  width: 340px;
  max-width: 90vw;
  max-height: 280px;
  overflow-y: auto;
  padding: ${space2};
`;

const SectionLabel = styled(Typography)`
  font-size: ${fontSizeXs};
  font-weight: ${fontWeightSemibold};
  color: ${colorAgentTextSecondary};
  margin-top: ${space1};
  margin-bottom: 2px;
`;

const MenuItemLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: ${fontSizeXs};
  color: ${colorAgentTextSecondary};
  text-decoration: none;

  &:hover {
    background-color: ${colorNeutralGray};
  }

  & .item-icon {
    font-size: 16px;
    flex-shrink: 0;
  }

  & .item-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

type CopyFeedbackProps = Pick<CopyFeedback, 'isCopied' | 'copyLabel' | 'handleCopy'>;

function MessageFooterActionRow({ copyFeedback }: { copyFeedback: CopyFeedbackProps }) {
  const { isCopied, copyLabel, handleCopy } = copyFeedback;
  const { isThumbUpSelected, isThumbDownSelected, handleThumbUpClick, handleThumbDownClick } =
    useMessageFeedbackSelection();

  return (
    <>
      <Tooltip title="良い回答" placement="top">
        <ActionButton
          size="small"
          onClick={handleThumbUpClick}
          aria-label="良い回答"
          aria-pressed={isThumbUpSelected}
        >
          <ActionIcon as={isThumbUpSelected ? ThumbUpIcon : ThumbUpOffAltIcon} />
        </ActionButton>
      </Tooltip>
      <Tooltip title="悪い回答" placement="top">
        <ActionButton
          size="small"
          onClick={handleThumbDownClick}
          aria-label="悪い回答"
          aria-pressed={isThumbDownSelected}
        >
          <ActionIcon as={isThumbDownSelected ? ThumbDownIcon : ThumbDownOffAltIcon} />
        </ActionButton>
      </Tooltip>
      <Tooltip title={copyLabel} placement="top">
        <ActionButton size="small" onClick={handleCopy} aria-label={copyLabel}>
          <ActionIcon
            as={isCopied ? CheckIcon : ContentCopyIcon}
            style={isCopied ? { color: colorSemanticSuccess } : undefined}
          />
        </ActionButton>
      </Tooltip>
    </>
  );
}

function SourceFileMenuItem({ link, icon }: { link: DownloadLink; icon: 'source' | 'download' }) {
  const isDownload = icon === 'download';
  return (
    <MenuItemLink
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      download={isDownload ? link.fileName : undefined}
      title={link.fileName}
    >
      {isDownload ? <FileDownloadIcon className="item-icon" /> : <DescriptionIcon className="item-icon" />}
      <span className="item-text">{getDownloadLinkDisplayName(link)}</span>
    </MenuItemLink>
  );
}

function SourceFileMenuSection({
  label,
  links,
  icon,
}: {
  label: string;
  links: DownloadLink[];
  icon: 'source' | 'download';
}) {
  if (links.length === 0) return null;
  return (
    <>
      <SectionLabel>{label}</SectionLabel>
      {links.map((link, index) => (
        <SourceFileMenuItem key={getDownloadLinkKey(link, index)} link={link} icon={icon} />
      ))}
    </>
  );
}

function SourceFileMenu({
  sourceLinks,
  downloadLinks,
  linkCount,
}: {
  sourceLinks: DownloadLink[];
  downloadLinks: DownloadLink[];
  linkCount: number;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleSourceMenuOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);
  const handleSourceMenuClose = useCallback(() => setAnchorEl(null), []);

  return (
    <>
      <Tooltip title={`情報源・ファイル（${linkCount}件）`} placement="top">
        <SourceButton onClick={handleSourceMenuOpen} aria-label="情報源・ファイルを表示">
          <MenuBookIcon className="source-icon" />
          <span>情報源・ファイル {linkCount}件</span>
        </SourceButton>
      </Tooltip>
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={handleSourceMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <MenuPaper>
          <SourceFileMenuSection label="情報源" links={sourceLinks} icon="source" />
          <SourceFileMenuSection label="ダウンロード" links={downloadLinks} icon="download" />
        </MenuPaper>
      </Popover>
    </>
  );
}

export function MessageFooter({ links, content }: Props) {
  const copyFeedback = useCopyFeedback(content);
  const linkGroups = splitDownloadLinks(links);
  const canShowActions = copyFeedback.canCopy;
  const canShowSourceMenu = hasDownloadLinks(linkGroups);

  if (!canShowActions && !canShowSourceMenu) return null;

  return (
    <ActionRow>
      {canShowActions && <MessageFooterActionRow copyFeedback={copyFeedback} />}
      {canShowSourceMenu && (
        <SourceFileMenu
          sourceLinks={linkGroups.sourceLinks}
          downloadLinks={linkGroups.downloadLinks}
          linkCount={countDownloadLinks(linkGroups)}
        />
      )}
    </ActionRow>
  );
}
